
import { useEffect, useState } from "react";

import { FilledButton, OutlinedButton } from "../../common/Button";


import useCurrentUser from "../../../hooks/useCurrentUser";
import { IFreelancer } from "../../../data_models/freelancer";
import Lightbox from "../../common/Lightbox";
import TextInputField from "../../common/TextInputField";

import { checkDateFormat, checkPhoneNumber } from "../../../utils/regularExpressions";
import { OutlinedDropdownMenu } from "../../common/DropdownMenu";
import { getStringEnumValues } from "../../../utils/enumUtils";
import { ECountry, countriesToTextMapper } from "../../../data_models/data/countries";
import MultiTextSelect from "../../common/MultiTextSelect";
import { dateToFormatString } from "../../../utils/dateUtils";


interface EditPersonalInfoFreelancerProps {
    onClose: () => void;
}

export const EditPersonalInfoFreelancer: React.FC<EditPersonalInfoFreelancerProps> = ({ onClose }) => {

    const [loading, setLoading] = useState(false);
    const { currentUser, updateCurrentUser } = useCurrentUser();


    const [firstName, setFirstName] = useState((currentUser as IFreelancer)?.firstName || '');
    const [middleName, setMiddleName] = useState((currentUser as IFreelancer)?.middleName || '');
    const [lastName, setLastName] = useState((currentUser as IFreelancer)?.lastName || '');
    const [languages, setLanguages] = useState((currentUser as IFreelancer)?.languages || []);

    const [phone, setPhone] = useState((currentUser as IFreelancer)?.phone || '');
    const [dateOfBirth, setDateOfBirth] = useState((currentUser as IFreelancer)?.birthDate ? dateToFormatString((currentUser as IFreelancer).birthDate!) : '')

    const [streetAddress, setStreetAddress] = useState((currentUser as IFreelancer)?.streetAddress || '');
    const [streetAddress2, setStreetAddress2] = useState((currentUser as IFreelancer)?.streetAddress2 || '');
    const [city, setCity] = useState((currentUser as IFreelancer)?.city || '');
    const [state, setState] = useState((currentUser as IFreelancer)?.state || '');
    const [zipCode, setZipCode] = useState((currentUser as IFreelancer)?.zipCode || '');
    const [country, setCountry] = useState((currentUser as IFreelancer)?.country || ECountry.UNITED_STATES);

    const [firstNameError, setFirstNameError] = useState('');
    const [lastNameError, setLastNameError] = useState('');
    const [dateOfBirthError, setDateOfBirthError] = useState('');
    const [phoneError, setPhoneError] = useState('');


    const checkIfValid = () => {
        setFirstNameError('');
        setLastNameError('');
        setDateOfBirthError('');
        setPhoneError('');



        let valid = true;
        // first name
        if (firstName.length < 2) {
            setFirstNameError('First name have at least 2 characters');
            valid = false;
        }
        // last name
        if (lastName.length < 2) {
            setFirstNameError('Last name have at least 2 characters');
            valid = false;
        }

        // // check date of birth
        // if (dateOfBirth && !checkDateFormat(dateOfBirth)) {
        //     setDateOfBirthError('Date of birth must be in the format yyyy/mm/dd');
        //     console.log('date of birth error');
        //     valid = false;
        // }

        // check phone
        if (phone && !checkPhoneNumber(phone) && phone !== '') {
            setPhoneError('This is not a valid number');
            valid = false;
        }


        return valid;
    }

    const onSubmit = async () => {

        const valid = checkIfValid();
        if (!valid) return;

        setLoading(true);

        try {

            const updateObj: IFreelancer = {
                firstName: firstName,
                middleName: middleName,
                lastName: lastName,
                phone: phone,
                birthDate: new Date(),
                languages: languages,

                streetAddress: streetAddress,
                streetAddress2: streetAddress2,
                city: city,
                state: state,
                zipCode: zipCode,
                country: country,
            }
            await updateCurrentUser(updateObj);
        } catch (error: any) {
        } finally {
            setLoading(false);
            onClose();
        }
    }

    return (
        <Lightbox
            content={
                <div className="flex flex-col space-y-6 w-full" >
                    <h4>Update Personal Information</h4>

                    {/* First Name */}
                    <TextInputField
                        type="text"
                        id="firstName"
                        label="First Name"
                        placeholder="First Name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        error={firstNameError}
                    />


                    {/* Middle Name */}
                    <TextInputField
                        type="text"
                        id="middleName"
                        label="Middle Name (optional)"
                        placeholder="Middle Name"
                        value={middleName}
                        onChange={(e) => setMiddleName(e.target.value)}
                    />


                    {/* Last Name */}
                    <TextInputField
                        type="text"
                        id="lastName"
                        label="Last Name"
                        placeholder="Last Name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        error={lastNameError}
                    />

                    <MultiTextSelect
                        elements={languages}
                        setElements={setLanguages}
                        label="Spoken Languages"
                    />

                    <div className="flex flex-col space-y-6 p-2 w-full bg-neutral-100 rounded-xl">
                        <p className="caption2 ">The following fields are only for legal or technical purposes and won't be shared with other users.</p>

                        {/* Phone */}
                        <TextInputField
                            type="text"
                            id="phone"
                            label="Phone"
                            placeholder="Phone Number"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            error={phoneError}
                        />

                        {/* Birthdate
                        <TextInputField
                            type="text"
                            id="birthYear"
                            label="Date of Birth (mm/dd/yyyy)"
                            placeholder="Date of Birth"
                            value={dateOfBirth}
                            onChange={(e) => setDateOfBirth(e.target.value)}
                            error={dateOfBirthError}
                        /> */}

                        <hr className="border-neutral-300 mx-10" />

                        {/* Street Address */}
                        <TextInputField
                            type="text"
                            id="streetAddress"
                            label="Street Address"
                            placeholder="Street Address"
                            value={streetAddress}
                            onChange={(e) => setStreetAddress(e.target.value)}
                        />

                        {/* Street Address 2 */}
                        <TextInputField
                            type="text"
                            id="streetAddress2"
                            label="Street Address 2 (optional)"
                            placeholder="Apt, Unit, Floor, Suite, etc."
                            value={streetAddress2}
                            onChange={(e) => setStreetAddress2(e.target.value)}
                        />

                        {/* City */}
                        <TextInputField
                            type="text"
                            id="city"
                            label="City"
                            placeholder="City"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        />

                        {/* State */}
                        <TextInputField
                            type="text"
                            id="state"
                            label="State"
                            placeholder="State"
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                        />

                        {/* Zipcode */}
                        <TextInputField
                            type="text"
                            id="zipCode"
                            label="Zip Code"
                            placeholder="Zip Code"
                            value={zipCode}
                            onChange={(e) => setZipCode(e.target.value)}
                        />

                        {/* Country */}
                        <OutlinedDropdownMenu
                            options={getStringEnumValues(ECountry)}
                            optionsToTextMapper={countriesToTextMapper}
                            onSelect={(value) => {
                                const val = value as unknown as ECountry;
                                setCountry(val)
                            }}
                            defaultValue={country}
                            label="Country"
                        />
                    </div>


                    <div className="flex flex-row justify-end w-full space-x-2">

                        <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
                        <FilledButton
                            isLoading={loading}
                            onClick={onSubmit}>Submit</FilledButton>
                    </div>
                </div>}
            onClose={onClose}
        />

    );
}

